import {
  type ScheduleModel,
  type ContractorGetCampaignModel,
  type ManagerGetCampaignModel,
  type Days,
  PendingIcon,
} from 'entities/campaign';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { showSuccessToast } from 'shared/lib/notifications';
import { getKeys } from 'shared/lib/object';
import { AlertBox } from 'shared/ui/AlertBox';
import { Button } from 'shared/ui/Button';
import { Switcher } from 'shared/ui/Switcher';
import { parseDateValue } from 'shared/lib/format';
import { AlertModal, SideModal, SideModalBody, SideModalFooter, useModalsActions } from 'shared/ui/SideModal';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { isValidationErrorsModel } from 'shared/lib/api';
import { form } from 'shared/lib/form';
import { Tooltip } from 'shared/ui/Tooltip';

import { useUpdateCallCenterSchedule } from '../api';

interface ScheduleModalProps {
  campaign: ManagerGetCampaignModel | ContractorGetCampaignModel;
  appliesAt: string | null;
  isShowClock: boolean;
  children: ReactNode;
}

const HIDDEN_ERROR = 'hidden';
const SUCCESS_MESSAGE = 'Call center hours were successfully updated.';

export function ScheduleModal({ children, campaign, appliesAt, isShowClock }: ScheduleModalProps) {
  if (!campaign.schedule) {
    throw new Error('Campaign call center schedule must be not null');
  }
  const [isScheduleOpen, setIsScheduleOpen] = useState(campaign.callCenterHasCustomSchedule);
  const { forceClose } = useModalsActions();
  const methods = useForm({ defaultValues: campaign.schedule });
  const updateScheduleQuery = useUpdateCallCenterSchedule(campaign.id);
  const { setError, handleSubmit, control, clearErrors } = methods;
  const scheduleForm = useWatch({ control }) as ScheduleModel;
  const showUpdateClock = appliesAt && isShowClock;

  const pendingChangeDate = appliesAt ? parseDateValue(appliesAt, 'onlyDate') : null;

  const switcherHandler = () => {
    clearErrors();
    setIsScheduleOpen((isOpen) => !isOpen);
  };

  const submit = handleSubmit((value) => {
    const scheduleValue = isScheduleOpen ? value : null;

    updateScheduleQuery.mutate(
      { payload: { schedule: scheduleValue } },
      {
        onSuccess: () => {
          forceClose();
          showSuccessToast(SUCCESS_MESSAGE);
        },
        onError: (error) => {
          const errorData = error.response.data;

          if (!isValidationErrorsModel(errorData)) {
            return;
          }
          const errors = errorData.errors;

          for (const key of getKeys(errors)) {
            const day = key as Days;
            const error = errors[key];

            if (error) {
              setError(day, { message: error[0].message });
            }

            setError(`${day}.timeTo`, { message: HIDDEN_ERROR });
            setError(`${day}.timeFrom`, { message: HIDDEN_ERROR });
          }
        },
      }
    );
  });

  const isAtLeastSelected = !getKeys(campaign.schedule)
    .map((day) => scheduleForm[day])
    .find((x) => x.isSelected);

  return (
    <SideModal autoTestAttributesName="call-center-hours-modal">
      <SideModalBody title="Call center hours">
        <FormProvider {...methods}>
          <AlertModal
            autoTestAttributesName="call-center-hours-exit-modal"
            showCondition={form(methods)}
          />
          <AlertBox
            className="border-deep-blue-100"
            iconClass="text-deep-blue-500"
            bodyClass="w-full"
          >
            <div className="flex items-center justify-between">
              <div className="text-sm font-normal text-slate-700">Add call center hours</div>
              <Switcher
                name="is_schedule_open"
                checked={isScheduleOpen}
                onChange={switcherHandler}
              />
            </div>
          </AlertBox>
          {isScheduleOpen && children}
        </FormProvider>
      </SideModalBody>

      <SideModalFooter>
        <Tooltip
          content={<div className="w-56 text-left text-xs">Please select at least one day to proceed</div>}
          placement="top-end"
          isDisabled={!isAtLeastSelected}
        >
          <Button
            onClick={submit}
            className="w-full"
            contentClassName="!text-left"
            disabled={isAtLeastSelected}
            iconLeftName={showUpdateClock ? 'update' : undefined}
            {...getDataAutoTestAttributes(['button-update-schedule'])}
          >
            Update schedule
          </Button>
        </Tooltip>
        {showUpdateClock && (
          <div className="mt-4 flex items-center gap-2">
            <PendingIcon />
            <div>Call center hours changes will be applied on Monday, {pendingChangeDate}</div>
          </div>
        )}
      </SideModalFooter>
    </SideModal>
  );
}
